import { createDeepSignal } from "@solid-primitives/resource";
import { stAnalytics } from "@storytell/analytics";
import {
  type AuthService,
  type AuthStore,
  emptyRealm,
  firebaseConfig,
  getAuthIsGuest,
  getEmailTypeFromDomain,
  initialAuthStore,
  isLifecycleDeleteRequested,
  isLifecycleError,
  isLifecycleLoading,
  isLifecycleMarkedForAbuse,
  isLifecyclePendingVerification,
  isLifecycleRegistered,
  type PostAuthAction,
} from "@storytell/auth";
import { useAuthNoOpMethods } from "@storytell/auth-noop-implementation";
import { dispatchGlobalEvent } from "@storytell/events";
import { type Logger, Named } from "@storytell/logger";
import { type FirebaseApp, initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { type Accessor, createEffect, createMemo, createResource, createSignal } from "solid-js";
import { getRequestEvent, isServer } from "solid-js/web";
import { useAuthBrowserMethods } from "./authBrowserMethods";
import { clientAuthResourceServer } from "./authResourceHandlerClient";
import { authResourceHandlerServer } from "./authResourceHandlerServer";
import { cookieStorage, makePersisted } from "@solid-primitives/storage";

// Auth service implementation for v3 web-app.
//
// It is designed to work both server-side and in the browser by leveraging
// different implementations of the firebase app.
//
// It's using a resource as the main store for the auth data so that it can trigger
// <Suspense> on the server and force it to wait until it's loaded before rendering the
// rest of the app and streaming it to the client.
//
// On the client, the logic inside the resource should normally not run, it is there
// to make hot module reloading work in dev mode. The actual logic that runs on the client
// is the setupListeners() method that will watch for auth state changes and mutate the
// resource __without__ triggering a resource reload and causing a global loading state.
export const useAuthWithSSRService = (deps: {
  logger: Logger;
  deviceId: Accessor<string>;
  locale: Accessor<string>;
  userAgent: Accessor<string>;
}): AuthService => {
  const logger = new Named(deps.logger, "authSSR");
  const event = getRequestEvent();

  // Doing it like this to avoid initializing the app on the server
  let firebaseApp: FirebaseApp;
  const getFirebaseClientAuth = () => {
    if (!firebaseApp) {
      firebaseApp = initializeApp(firebaseConfig());
      return getAuth(firebaseApp);
    }
    return getAuth(firebaseApp);
  };

  const resource = createResource(
    (): Promise<AuthStore> =>
      isServer
        ? authResourceHandlerServer({
            event,
            logger,
            deviceId: deps.deviceId(),
            locale: deps.locale(),
            userAgent: deps.userAgent(),
          })
        : clientAuthResourceServer({
            logger,
            deviceId: deps.deviceId(),
            locale: deps.locale(),
            userAgent: deps.userAgent(),
            getFirebaseClientAuth,
          }),
    {
      deferStream: true,
      initialValue: initialAuthStore(),
      storage: createDeepSignal,
    },
  );

  const [data] = resource;

  // --- UI ONLY STATES ---

  // isLoading is a UI only state that returns true when we are attempting to
  // determine the auth state of the user.
  const isLoading = () => isLifecycleLoading(data().state);

  // isError is a UI only state that returns true when we failed to determine
  // the authentication state
  const isError = () => isLifecycleError(data().state);

  // isConnecting is a UI only state that returns true when the system is
  // loading the auth state or when we have experienced an error. In this state
  // the application is not usable.
  const isConnecting = () => isLoading() || isError();

  // isReady returns true if the auth service is ready to be used (e.g., we
  // are no longer connecting)
  const isReady = () => !isConnecting();

  // --- USER STATES ---

  // isGuest returns true when the user is unidentified.
  const isGuest = () => getAuthIsGuest(data());

  // isDeleteRequested returns true when a user has asked for their account to
  // be deleted.
  const isDeleteRequested = () => isLifecycleDeleteRequested(data().state);

  // isMarkedForAbuse returns true if the user has been marked for abuse
  // and should not be allowed access to the system.
  const isMarkedForAbuse = () => isLifecycleMarkedForAbuse(data().state);

  // isPendingVerification returns true when a user is identified but has not
  // yet confirmed their email address.
  const isPendingVerification = () => isLifecyclePendingVerification(data().state);

  // isRegistered returns true when a user is identified and their email address
  // has been verified by the platform or the authentication provider.
  const isRegistered = () => isLifecycleRegistered(data().state);

  // --- HELPFUL ACCESSORS ---

  // realm returns the user's working context.
  const realm = () => data().authenticatedUser?.realm ?? emptyRealm();

  // availableRealms returns other realms the user may choose to work within.
  const availableRealms = () => data().authenticatedUser?.availableRealmList ?? [];

  // authenticatedUser returns the authenticated user object which wraps
  // the user and has additional information about the last auth-sync operation
  const authenticatedUser = () => data().authenticatedUser;

  // user returns the user object which is common to all user operations.
  // This is a convenience method to avoid having to call authenticatedUser()
  // and then reference the embedded user.
  const user = () => data().authenticatedUser?.user;

  const userId = () => data()?.authenticatedUser?.user?.id as `user_${string}` | undefined;

  // error returns any error that occurred during the authentication process.
  const error = () => data().error;

  // token returns the active bearer token for the user.
  const token = () => data().token;

  // Actions that storytell should perform automatically after a successful sign-in/up
  // Like taking a user back to refilling their credits after they got redirected to auth
  // when trying to do so from a guest account.
  const [postAuthAction, setPostAuthAction] = makePersisted(createSignal<PostAuthAction>(), {
    storage: cookieStorage,
    name: "authRedirect:v1",
    storageOptions: {
      path: "/",
      sameSite: "Lax",
      expires: new Date(Date.now() + 15 * 60 * 1000), // 15 minutes from now
    },
  });

  const methods = isServer
    ? useAuthNoOpMethods()
    : useAuthBrowserMethods({
        logger: deps.logger,
        resource,
        deviceId: deps.deviceId,
        locale: deps.locale,
        userAgent: deps.userAgent,
      });

  const signOut = async () => {
    dispatchGlobalEvent("sign-out", undefined);
    setTimeout(() => {
      methods.signOut();
    }, 100);
  };

  methods.setupListeners();

  createEffect(() => {
    if (isRegistered()) {
      const u = user();
      if (!u) return;

      try {
        stAnalytics.identify(u.id, {
          name: u.displayName,
          email: u.primaryEmail,
          emailType: getEmailTypeFromDomain(u.primaryEmail),
        });
      } catch (error) {
        // todo: capture with sentry but don't allow errors to impact user
      }
    }
  });

  return {
    userId,
    isGuest,
    isDeleteRequested,
    isLoading,
    isError,
    isReady,
    realm,
    isPendingVerification,
    authenticatedUser,
    availableRealms,
    isRegistered,
    user,
    error,
    token,
    isConnecting,
    isMarkedForAbuse,
    postAuthAction,
    setPostAuthAction,

    methods: {
      ...methods,
      signOut,
    },
  };
};
